import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config';
import { useNotification } from '../Notification';

interface ImportListModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchId: string;
  onSuccess: () => void;
}

interface ChangeSummary {
  playersAdded: any[];
  playersRemoved: any[];
  positionChanges: any[];
  teamChanges: any[];
  totalChanges: number;
}

interface PreviewData {
  newTeam1: any[];
  newTeam2: any[];
  newSubstitutes: any[];
}

export const ImportListModal: React.FC<ImportListModalProps> = ({
  isOpen,
  onClose,
  matchId,
  onSuccess
}) => {
  const [listText, setListText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  const [comparison, setComparison] = useState<{
    changes: ChangeSummary;
    preview: PreviewData;
    conflicts: any[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showNotification } = useNotification();

  // Reiniciar estado cuando el modal se abra
  useEffect(() => {
    if (isOpen) {
      resetModalState();
    }
  }, [isOpen]);

  const handleCompare = async () => {
    if (!listText.trim()) {
      setError('Por favor ingresa la lista para comparar');
      return;
    }

    setIsComparing(true);
    setError(null);

    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(getApiUrl(`/api/matches/${matchId}/parse-and-compare`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ listText: listText.trim() })
      });



      if (response.ok) {
        const data = await response.json();
        setComparison(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al comparar la lista');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Debug - Fetch error:', error);
      setError('Error de conexión al comparar la lista');
    } finally {
      setIsComparing(false);
    }
  };

  const handleApplyChanges = async () => {
    if (!comparison) {
      setError('Primero debes comparar la lista');
      return;
    }

    setIsLoading(true);
    setError(null);

    const token = localStorage.getItem('authToken');

    try {
      const response = await fetch(getApiUrl(`/api/matches/${matchId}/apply-imported-list`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          listText: listText.trim(),
          confirmedChanges: true 
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Assuming showNotification is defined elsewhere or will be added
        // alert(`✅ Lista importada exitosamente!\n\n${data.summary.join('\n')}`);
        onSuccess();
        resetModalState(); // Reiniciar estado antes de cerrar
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al aplicar los cambios');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error applying changes:', error);
      setError('Error de conexión al aplicar los cambios');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModalState = () => {
    setListText('');
    setComparison(null);
    setError(null);
    setIsLoading(false);
    setIsComparing(false);
  };

  const handleClose = () => {
    resetModalState();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#23272A',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #33383B'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ color: '#FFFFFF', margin: 0 }}>📋 Importar Lista</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#FFFFFF',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#CCCCCC', marginBottom: '12px' }}>
            Pega aquí la lista actualizada del grupo (WhatsApp, Telegram, etc.) <strong>Recuerda usar primero el exportar lista para mantener el formato correcto</strong> 
          </p>
          <textarea
            value={listText}
            onChange={(e) => setListText(e.target.value)}
            placeholder={`📍 Lugar: Sucre
📅 Fecha: 31-08-2025
⏰ Hora: 07:30
💰 Precio total: $123
💸 Precio por persona: $10.25

⚽ EQUIPOS:
🔵 Equipo 1
1. Ezequiel Romanelli
2. Ezequiel Romanelli TEST 6
...

🔴 Equipo 2
1. Ezequiel Romanelli 2
2. Ezequiel Romanelli invitao
...

🟠 SUPLENTES:
1. Ezequiel Romanelli test`}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              height: '200px',
              backgroundColor: '#2C2F33',
              border: '1px solid #33383B',
              borderRadius: '8px',
              padding: '12px',
              color: '#FFFFFF',
              fontSize: '16px',
              fontFamily: 'monospace',
              resize: 'vertical'
            }}
          />
        </div>

        {error && (
          <div style={{
            backgroundColor: '#FF1744',
            color: '#FFFFFF',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <button
            onClick={handleCompare}
            disabled={isComparing || !listText.trim()}
            style={{
              backgroundColor: '#4CAF50',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: isComparing || !listText.trim() ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              opacity: isComparing || !listText.trim() ? 0.6 : 1
            }}
          >
            {isComparing ? 'Comparando...' : '🔍 Comparar Lista'}
          </button>

          {comparison && (
            <button
              onClick={handleApplyChanges}
              disabled={isLoading}
              style={{
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                opacity: isLoading ? 0.6 : 1
              }}
            >
              {isLoading ? 'Aplicando...' : '✅ Aplicar Cambios'}
            </button>
          )}
        </div>

        {comparison && (
          <div style={{
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            padding: '20px',
            border: '1px solid #33383B'
          }}>
            <h3 style={{ color: '#FFFFFF', marginTop: 0, marginBottom: '16px' }}>
              📊 Resumen de Cambios ({comparison.changes.totalChanges})
            </h3>

            {comparison.changes.playersAdded.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ color: '#4CAF50', marginBottom: '8px' }}>✅ Jugadores a Agregar:</h4>
                <ul style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                  {comparison.changes.playersAdded.map((player, index) => (
                    <li key={index}>
                      {player.name} 
                      {player.isSubstitute ? ' (Suplente)' : ` (Equipo ${player.teamId}, Posición ${player.teamPosition + 1})`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {comparison.changes.playersRemoved.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ color: '#FF1744', marginBottom: '8px' }}>❌ Jugadores a Remover:</h4>
                <ul style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                  {comparison.changes.playersRemoved.map((player, index) => (
                    <li key={index}>{player.name}</li>
                  ))}
                </ul>
              </div>
            )}

            {comparison.changes.positionChanges.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ color: '#FF9800', marginBottom: '8px' }}>🔄 Cambios de Posición:</h4>
                <ul style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                  {comparison.changes.positionChanges.map((change, index) => (
                    <li key={index}>
                      {change.player.name}: Equipo {change.oldTeam} → Equipo {change.newTeam}, 
                      Posición {change.oldPosition + 1} → Posición {change.newPosition + 1}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div style={{
              backgroundColor: '#1E2124',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '16px'
            }}>
              <h4 style={{ color: '#FFFFFF', marginTop: 0, marginBottom: '12px' }}>👀 Vista Previa:</h4>
              
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <h5 style={{ color: '#4CAF50', marginBottom: '8px' }}>🔵 Equipo 1:</h5>
                  <ol style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                    {comparison.preview.newTeam1.map((player, index) => (
                      <li key={index} style={{ 
                        color: player && player.isNew ? '#FF9800' : '#CCCCCC',
                        fontWeight: player && player.isNew ? 'bold' : 'normal'
                      }}>
                        {player ? player.name : '(Lugar libre)'}
                        {player && player.isNew && ' (NUEVO)'}
                      </li>
                    ))}
                  </ol>
                </div>

                <div style={{ flex: 1 }}>
                  <h5 style={{ color: '#FF1744', marginBottom: '8px' }}>🔴 Equipo 2:</h5>
                  <ol style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                    {comparison.preview.newTeam2.map((player, index) => (
                      <li key={index} style={{ 
                        color: player && player.isNew ? '#FF9800' : '#CCCCCC',
                        fontWeight: player && player.isNew ? 'bold' : 'normal'
                      }}>
                        {player ? player.name : '(Lugar libre)'}
                        {player && player.isNew && ' (NUEVO)'}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              {comparison.preview.newSubstitutes.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <h5 style={{ color: '#FF9800', marginBottom: '8px' }}>🟠 Suplentes:</h5>
                  <ol style={{ color: '#CCCCCC', margin: 0, paddingLeft: '20px' }}>
                    {comparison.preview.newSubstitutes.map((player, index) => (
                      <li key={index} style={{ 
                        color: player && player.isNew ? '#FF9800' : '#CCCCCC',
                        fontWeight: player && player.isNew ? 'bold' : 'normal'
                      }}>
                        {player ? player.name : '(Lugar libre)'}
                        {player && player.isNew && ' (NUEVO)'}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
